import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import "./css/PortalImport.css";

const fmtBytes = (b) => {
  if (!b || isNaN(b)) return "";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

// Free-form download: paste any public http(s) file URLs and download them into a project.
// Cost is size-based and charged AFTER the download completes (from actual bytes), like the
// other public-data sources. Status is tracked on the shared /downloads page.
const CustomUrlImportPage = () => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [projectName, setProjectName] = useState("");
  const [checking, setChecking] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [checked, setChecked] = useState(null); // validation result: { items, creditsPerGb, balance }
  const [message, setMessage] = useState(null);  // {type,text}

  const parsedUrls = () =>
    Array.from(new Set(text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)));

  const validName = () => /^[A-Za-z0-9_]+$/.test(projectName.trim());

  const check = async () => {
    const urls = parsedUrls();
    setMessage(null); setChecked(null);
    if (!urls.length) { setMessage({ type: "error", text: "Paste at least one URL." }); return; }
    if (urls.length > 50) { setMessage({ type: "error", text: "Maximum 50 URLs per request." }); return; }
    setChecking(true);
    try {
      const res = await axios.post(`${API_BASE}/api/public-data/validate`,
        { source: "url", ids: urls }, { withCredentials: true });
      setChecked(res.data);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.error || "Could not check the URLs." });
    } finally { setChecking(false); }
  };

  const download = async () => {
    const urls = parsedUrls();
    setMessage(null);
    if (!validName()) { setMessage({ type: "error", text: "Enter a project name (letters, numbers, underscores)." }); return; }
    if (!urls.length) { setMessage({ type: "error", text: "Paste at least one URL." }); return; }
    if (urls.length > 50) { setMessage({ type: "error", text: "Maximum 50 URLs per request." }); return; }
    setLaunching(true);
    try {
      const res = await axios.post(`${API_BASE}/api/public-data/import`,
        { source: "url", projectName: projectName.trim(), ids: urls }, { withCredentials: true });
      const skipped = (res.data.skipped || []).length;
      setMessage({ type: "success", text: `Download started for ${res.data.summary?.okCount || urls.length} file(s)${skipped ? ` (${skipped} skipped)` : ""}. Redirecting…` });
      setTimeout(() => navigate("/downloads", { state: { message: "Your download request has been submitted." } }), 900);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.error || "Failed to start download." });
    } finally { setLaunching(false); }
  };

  const okItems = (checked?.items || []).filter((i) => i.public);
  const estBytes = okItems.reduce((a, i) => a + (i.bytes || 0), 0);

  return (
    <div className="portal-import">
      <h1>Download from URLs</h1>
      <p className="portal-intro">
        Paste any public file URLs (one per line) and we'll download them into your project.
        Cost is based on the downloaded file size and is charged after the download completes.
        Track progress on your <button className="portal-linkbtn" onClick={() => navigate("/downloads")}>downloads</button> page.
      </p>

      <div className="portal-step">
        <div className="portal-step-title"><span className="portal-step-num">1</span>Paste file URLs</div>
        <textarea
          className="portal-search"
          style={{ width: "100%", minHeight: 160, fontFamily: "monospace", fontSize: 13, padding: 10, boxSizing: "border-box" }}
          placeholder={"https://example.org/genome.fa.gz\nhttps://example.org/annotations.gtf.gz"}
          value={text}
          onChange={(e) => { setText(e.target.value); setChecked(null); }}
        />
        <div className="portal-count">{parsedUrls().length} URL(s) · max 50 per request · http/https only</div>
        <div className="portal-controls">
          <button className="portal-btn" disabled={checking || !parsedUrls().length} onClick={check}>
            {checking ? "Checking…" : "Check URLs (optional)"}
          </button>
        </div>
      </div>

      {checked && (
        <div className="portal-step">
          <div className="portal-step-title"><span className="portal-step-num">2</span>Reachability &amp; estimated size</div>
          <div className="portal-count">
            {okItems.length} of {checked.items.length} reachable · est. {fmtBytes(estBytes)}
            {checked.balance != null ? ` · balance ${checked.balance} credits` : ""}
          </div>
          <div className="portal-list">
            {checked.items.map((it) => (
              <div className="portal-file" key={it.id}>
                <label style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ color: it.public ? "#166534" : "#b91c1c", fontWeight: 600, marginRight: 8 }}>
                    {it.public ? "OK" : "SKIP"}
                  </span>
                  {it.file_name || it.id}
                  {!it.public && it.reason ? <span className="portal-row-sub"> — {it.reason}</span> : null}
                </label>
                <span className="portal-file-size">{fmtBytes(it.bytes)}</span>
              </div>
            ))}
          </div>
          <div className="portal-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Sizes are estimates from the server (HEAD); the final charge uses the actual downloaded bytes.
          </div>
        </div>
      )}

      <div className="portal-actions">
        <input className="portal-project" placeholder="Project name (a-z, 0-9, _)" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
        <button className="portal-btn" disabled={launching || !parsedUrls().length} onClick={download}>
          {launching ? "Starting…" : `Download ${parsedUrls().length || ""} file(s)`}
        </button>
        {message && <span className={message.type === "error" ? "portal-error" : "portal-success"}>{message.text}</span>}
      </div>
    </div>
  );
};

export default CustomUrlImportPage;
